"use client"

import React from "react"
import { AlertTriangle, RefreshCw, Home } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundaryClass extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Report error to Sentry
    import('@sentry/browser').then(({ captureException }) => {
      captureException(error, {
        tags: { context: 'ErrorBoundary' },
        extra: { componentStack: errorInfo.componentStack }
      })
    }).catch(() => {})
  }

  render() {
    if (this.state.hasError) {
      const resetError = () => {
        this.setState({ hasError: false, error: null })
      }

      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback
        return <FallbackComponent error={this.state.error!} resetError={resetError} />
      }

      return <DefaultErrorFallback error={this.state.error!} resetError={resetError} />
    }

    return this.props.children
  }
}

function DefaultErrorFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--tls-canvas)] p-4">
      <Card className="max-w-md w-full border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)]">
        <CardHeader className="text-center">
          <div className="mb-4 flex size-16 items-center justify-center rounded-[var(--radius-pill)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)] mx-auto">
            <AlertTriangle className="size-8 text-[var(--tls-fg-strong)]" />
          </div>
          <CardTitle className="font-display text-xl">This part didn&apos;t load</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-[var(--tls-fg-muted)]">
            A section of the page failed to render. Try again, or refresh if it keeps happening.
          </p>
          
          {process.env.NODE_ENV === 'development' && (
            <details className="rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-strong)] bg-[var(--tls-surface-elevated)] p-3">
              <summary className="cursor-pointer text-sm font-bold text-[var(--tls-fg-strong)]">
                Error Details (Development)
              </summary>
              <pre className="text-[var(--tls-fg-muted)] text-xs mt-2 overflow-auto max-h-32">
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}
          
          <div className="flex gap-2 justify-center">
            <Button
              onClick={resetError}
              className="font-display"
            >
              <RefreshCw className="size-4 mr-2" />
              Try Again
            </Button>
            <Button
              onClick={() => window.location.href = '/'}
              variant="outline"
              className="font-display"
            >
              <Home className="size-4 mr-2" />
              Go Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function ErrorBoundary({ children, fallback }: ErrorBoundaryProps) {
  return (
    <ErrorBoundaryClass fallback={fallback}>
      {children}
    </ErrorBoundaryClass>
  )
}
